import { useState } from 'react';
import Form from 'react-bootstrap/Form';
import Alert from 'react-bootstrap/Alert';
import { useSaveBrandVoiceMutation } from '../../store/api/brandMagic.api';
import { Button, Spinner } from 'react-bootstrap';
import { brandVoiceLabels, brandVoicePlaceHolders } from '../../constants/brandMagic';
import AutoResizeTextarea from './AutoResizeableTextArea';
import { motion, AnimatePresence } from 'framer-motion';
import { useAppSelector } from '../../store/store';
import { getActiveBrand } from '../../store/selector/brand.selector';
import { IBrandVoiceState } from '../../interfaces/brand-magic.interface';

type BrandVoiceProps = {
    handleNext: () => void;
    handlePrev: () => void;
    error: string;
    isFetchingBrandVoice: boolean;
    successMessage: string;
    brandVoice: IBrandVoiceState;
    setBrandVoice: React.Dispatch<React.SetStateAction<IBrandVoiceState>>;
};

const BrandVoice: React.FC<BrandVoiceProps> = ({
    handleNext,
    handlePrev,
    error,
    isFetchingBrandVoice,
    successMessage,
    brandVoice,
    setBrandVoice,
}) => {
    const [saveBrandVoice] = useSaveBrandVoiceMutation();
    const [isEditing, setIsEditing] = useState(false);
    const [formSubmitted, setFormSubmitted] = useState(false);
    const [formError, setFormError] = useState('');

    const handleClick = (event: any) => {
        event?.preventDefault();
        setIsEditing(true);
    };

    const handleInputChange = (event: any) => {
        setIsEditing(true);
        const { id, value } = event.target;
        setBrandVoice((prevBrandVoice) => ({
            ...prevBrandVoice,
            [id]: value,
        }));
    };

    const handleNextClick = async () => {
        setFormSubmitted(true);
        if (checkFormValidity()) {
            setIsEditing(false);

            handleNext();
            await saveBrandVoice(brandVoice)
                .unwrap()
                .then((response: any) => {})
                .catch((error) => {});
        } else {
            setFormError(
                'This information is necessary for Pixii to understand your brand and curate on-brand ads for you.',
            );
        }
    };
    const checkFormValidity = () => {
        const { scrapedTextContent, anythingElse, ...rest } = brandVoice;
        return Object.values(rest).every((value) => {
            if (typeof value === 'string') {
                return value.trim().length > 0;
            }
            return true;
        });
    };
    return (
        <>
            <div className="brandMagic_content">
                <AnimatePresence>
                    {error && (
                        <motion.div
                            initial={{ opacity: 0, y: -20 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -20 }}
                            transition={{ duration: 0.2 }}
                        >
                            <Alert variant="danger" dismissible>
                                <p style={{ fontSize: '0.85rem' }}>{error}</p>
                                <p className="mt-2 fst-italic" style={{ fontSize: '0.85rem' }}>
                                    <strong>Note:</strong> Every field is required.
                                </p>
                            </Alert>
                        </motion.div>
                    )}
                </AnimatePresence>
                <AnimatePresence>
                    {successMessage && (
                        <motion.div
                            initial={{ opacity: 0, y: -20 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -20 }}
                            transition={{ duration: 0.2 }}
                        >
                            <Alert variant="primary" dismissible>
                                <p style={{ fontSize: '0.85rem' }}>{successMessage}</p>

                                <p style={{ fontSize: '0.85rem' }}>{error}</p>
                                <p className="mt-2 fst-italic" style={{ fontSize: '0.85rem' }}>
                                    <strong>Note:</strong> Every field is required.
                                </p>
                            </Alert>
                        </motion.div>
                    )}
                </AnimatePresence>

                <AnimatePresence>
                    {formError && (
                        <motion.div
                            initial={{ opacity: 0, y: -20 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -20 }}
                            transition={{ duration: 0.2 }}
                        >
                            <Alert variant="danger" dismissible>
                                <Alert.Heading>Please fill in all fields.</Alert.Heading>
                                <p>{formError}</p>
                            </Alert>
                        </motion.div>
                    )}
                </AnimatePresence>
                <div className="mb-3">
                    <h1 className="fw-bold text-primary mb-3">Brand Voice</h1>
                </div>

                {false ? (
                    <div className="w-100 d-flex justify-content-center flex-column align-items-center mt-3">
                        <Spinner animation="border" style={{ color: '#967AA7' }} />
                        <span className="fst-italic text-primary h4 mt-1">
                            Reading your website...
                        </span>
                    </div>
                ) : (
                    <>
                        <div className="content_potions">
                            <div className="potion-alt">
                                {isFetchingBrandVoice ? (
                                    <div className="d-flex justify-content-center mt-3">
                                        <Spinner animation="border" />
                                    </div>
                                ) : (
                                    Object.entries(brandVoice).map(([field, value]) => {
                                        return (
                                            <>
                                                {field === 'scrapedTextContent' ||
                                                field === 'anythingElse' ||
                                                field === 'brandId' ? (
                                                    <></>
                                                ) : (
                                                    <Form.Group
                                                        key={field}
                                                        className="form mb-4 mt-2"
                                                        onClick={handleClick}
                                                    >
                                                        <Form.Label className="h4 text-primary fw-bold m-0">
                                                            {brandVoiceLabels[field]}
                                                        </Form.Label>
                                                        <div className="whitespace"></div>
                                                        <AutoResizeTextarea
                                                            value={value}
                                                            className={`${field}`}
                                                            id={field}
                                                            onChange={handleInputChange}
                                                            onBlur={() => {}}
                                                            rows={1}
                                                            readOnly={!isEditing}
                                                            placeholder={
                                                                brandVoicePlaceHolders[field]
                                                            }
                                                            required={true}
                                                            formSubmitted={formSubmitted}
                                                        />
                                                    </Form.Group>
                                                )}
                                            </>
                                        );
                                    })
                                )}
                            </div>
                        </div>
                    </>
                )}
            </div>
            {!isFetchingBrandVoice && (
                <div className="d-flex justify-content-between align-items-center mt-3 brandMagic_buttons">
                    <Button onClick={handlePrev} variant="secondary" size="lg">
                        Back
                    </Button>
                    <Button variant="primary" size="lg" onClick={handleNextClick}>
                        Next
                    </Button>
                </div>
            )}
        </>
    );
};

export default BrandVoice;
